<template>
  <div class="order-management">
    <!-- 页面标题和操作按钮 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">订单管理</h2>
        <p class="page-subtitle">管理平台所有订单信息</p>
      </div>
      <div class="header-right">
        <el-button type="primary" @click="handleExportOrders">
          <el-icon><Download /></el-icon>
          导出订单
        </el-button>
      </div>
    </div>

    <!-- 筛选和搜索区域 -->
    <div class="filter-section">
      <el-card shadow="never" class="filter-card">
        <div class="filter-content">
          <!-- 快速筛选 -->
          <div class="quick-filters">
            <el-radio-group v-model="filterParams.status" @change="handleFilterChange">
              <el-radio-button label="">全部订单</el-radio-button>
              <el-radio-button label="pending">待支付</el-radio-button>
              <el-radio-button label="paid">已支付</el-radio-button>
              <el-radio-button label="completed">已完成</el-radio-button>
              <el-radio-button label="cancelled">已取消</el-radio-button>
              <el-radio-button label="expired">已过期</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 高级筛选 -->
          <div class="advanced-filters">
            <el-form :model="filterParams" inline>
              <el-form-item label="订单类型">
                <el-select
                  v-model="filterParams.orderType"
                  placeholder="选择订单类型"
                  clearable
                  style="width: 150px"
                  @change="handleFilterChange"
                >
                  <el-option label="报名费" value="activity" />
                  <el-option label="会员购买" value="membership" />
                  <el-option label="赞助费" value="sponsor" />
                </el-select>
              </el-form-item>

              <el-form-item label="支付方式">
                <el-select
                  v-model="filterParams.paymentMethod"
                  placeholder="选择支付方式"
                  clearable
                  style="width: 150px"
                  @change="handleFilterChange"
                >
                  <el-option label="微信支付" value="wechat" />
                  <el-option label="支付宝" value="alipay" />
                  <el-option label="银行卡" value="bank" />
                </el-select>
              </el-form-item>

              <el-form-item label="订单时间">
                <el-date-picker
                  v-model="filterParams.orderDateRange"
                  type="daterange"
                  range-separator="至"
                  start-placeholder="开始日期"
                  end-placeholder="结束日期"
                  @change="handleFilterChange"
                  value-format="YYYY-MM-DD"
                />
              </el-form-item>

              <el-form-item>
                <el-button @click="resetFilters">重置</el-button>
              </el-form-item>
            </el-form>
          </div>

          <!-- 搜索框 -->
          <div class="search-box">
            <el-input
              v-model="filterParams.keyword"
              placeholder="搜索订单号、用户昵称或手机号"
              prefix-icon="Search"
              @input="handleSearch"
              clearable
              class="search-input"
            />
          </div>
        </div>
      </el-card>
    </div>

    <!-- 数据统计卡片（统一响应式网格） -->
    <div class="stats-grid">
      <el-card v-for="stat in orderStats" :key="stat.title" shadow="never" class="stat-card">
        <div class="stat-content">
          <div class="stat-icon">
            <el-icon :size="22"><component :is="stat.icon" /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-title">{{ stat.title }}</div>
            <div class="stat-value">{{ stat.value }}</div>
            <div v-if="stat.desc" class="stat-desc">{{ stat.desc }}</div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 退款项 -->
    <div class="refund-section">
      <el-card shadow="never">
        <template #header>
          <div class="table-header">
            <span>退款项</span>
            <div class="refund-summary">
              <span>退款总额：<b style="color: #F56C6C;">¥{{ Number(refundStats.totalRefund || 0).toFixed(2) }}</b></span>
              <span style="margin-left: 16px;">退款笔数：{{ refundStats.successCount }} 笔 / 共 {{ refundStats.count }} 条</span>
            </div>
          </div>
        </template>

        <el-table
          v-if="refundStats.recent && refundStats.recent.length > 0"
          :data="refundStats.recent"
          stripe
          size="small"
          style="width: 100%"
        >
          <el-table-column label="退款金额" width="130">
            <template #default="{ row }">
              <span style="color: #F56C6C; font-weight: 600;">¥{{ Number(row.amount || 0).toFixed(2) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="refund_reason" label="退款原因" min-width="220" show-overflow-tooltip />
          <el-table-column prop="user_nickname" label="用户" width="150">
            <template #default="{ row }">
              {{ row.user_nickname || '用户#' + row.user_id }}
            </template>
          </el-table-column>
          <el-table-column label="退款时间" width="190">
            <template #default="{ row }">
              {{ formatDateTime(row.refund_time || row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column label="退款状态" width="110">
            <template #default="{ row }">
              <el-tag :type="row.refund_status === 'success' ? 'success' : 'danger'" size="small">
                {{ row.refund_status === 'success' ? '退款成功' : '退款失败' }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-else description="暂无退款记录" :image-size="60" />
      </el-card>
    </div>

    <!-- 订单列表 -->
    <div class="list-section">
      <el-card shadow="never">
        <template #header>
          <div class="table-header">
            <span>订单列表</span>
            <div class="table-actions">
              <el-tooltip content="刷新数据" placement="top">
                <el-button circle @click="loadOrders">
                  <el-icon><Refresh /></el-icon>
                </el-button>
              </el-tooltip>
            </div>
          </div>
        </template>

        <el-table
          :data="orderList"
          v-loading="loading"
          stripe
          style="width: 100%"
        >
          <el-table-column type="expand">
            <template #default="{ row }">
              <div class="expand-content">
                <el-descriptions :column="2" border>
                  <el-descriptions-item label="订单ID">
                    {{ row.id }}
                  </el-descriptions-item>
                  <el-descriptions-item label="创建时间">
                    {{ formatDateTime(row.createTime) }}
                  </el-descriptions-item>
                  <el-descriptions-item label="支付时间">
                    {{ formatDateTime(row.payTime) }}
                  </el-descriptions-item>
                  <el-descriptions-item label="完成时间">
                    {{ formatDateTime(row.completeTime) }}
                  </el-descriptions-item>
                </el-descriptions>
                
                <!-- 订单商品信息 -->
                <div class="items-section" v-if="row.items && row.items.length > 0">
                  <h4>订单商品</h4>
                  <el-table :data="row.items" size="small" border>
                    <el-table-column prop="name" label="商品名称" />
                    <el-table-column prop="quantity" label="数量" width="80" />
                    <el-table-column prop="price" label="单价" width="100">
                      <template #default="{ row: item }">
                        ¥{{ item.price }}
                      </template>
                    </el-table-column>
                    <el-table-column label="小计" width="100">
                      <template #default="{ row: item }">
                        ¥{{ (item.price * item.quantity).toFixed(2) }}
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="订单信息" min-width="200">
            <template #default="{ row }">
              <div class="order-info">
                <div class="order-no">
                  <span class="label">订单号:</span>
                  <span class="value">{{ row.orderNo }}</span>
                </div>
                <div class="order-user">
                  <el-avatar :size="24" src="" />
                  <span class="user-name">{{ row.username }}</span>
                  <span class="user-phone">{{ row.phone }}</span>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="orderType" label="订单类型" width="100">
            <template #default="{ row }">
              <el-tag :type="getOrderTypeTag(row.orderType)">
                {{ getOrderTypeLabel(row.orderType) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="订单金额" width="120">
            <template #default="{ row }">
              <div class="order-amount">
                <span class="total">¥{{ row.totalAmount }}</span>
                <span class="discount" v-if="row.discountAmount > 0">
                  -¥{{ row.discountAmount }}
                </span>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="paymentMethod" label="支付方式" width="100">
            <template #default="{ row }">
              <el-tag size="small" :type="getPaymentMethodTag(row.paymentMethod)">
                {{ getPaymentMethodLabel(row.paymentMethod) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="status" label="订单状态" width="100">
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.status)" effect="light">
                {{ getStatusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="200" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleViewOrder(row)">
                详情
              </el-button>
              <el-dropdown>
                <el-button size="small">
                  更多
                  <el-icon><ArrowDown /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item 
                      @click="handleRefundOrder(row)"
                      :disabled="!['paid', 'completed'].includes(row.status)"
                    >
                      <el-icon><Money /></el-icon>
                      退款
                    </el-dropdown-item>
                    <el-dropdown-item 
                      @click="handleCancelOrder(row)"
                      :disabled="row.status !== 'pending'"
                    >
                      <el-icon><Close /></el-icon>
                      取消订单
                    </el-dropdown-item>
                    <el-dropdown-item 
                      @click="handleExportOrder(row)"
                    >
                      <el-icon><Document /></el-icon>
                      导出订单
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination
            v-model:current-page="pagination.current"
            v-model:page-size="pagination.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessageBox, ElMessage } from 'element-plus'
import api from '@/utils/api'

export default {
  name: 'OrderManagement',
  
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const orderList = ref([])
    
    // 筛选参数
    const filterParams = reactive({
      status: '',
      orderType: '',
      paymentMethod: '',
      orderDateRange: [],
      keyword: ''
    })
    
    // 分页参数
    const pagination = reactive({
      current: 1,
      size: 10,
      total: 0
    })
    
    // 订单统计
    const orderStats = ref([
      {
        title: '总订单数',
        value: '0',
        icon: 'Document',
        color: '#409EFF'
      },
      {
        title: '今日订单',
        value: '0',
        icon: 'Calendar',
        color: '#67C23A'
      },
      {
        title: '待支付',
        value: '0',
        icon: 'Clock',
        color: '#E6A23C'
      },
      {
        title: '活动订单实收',
        value: '¥0',
        icon: 'Coin',
        color: '#409EFF',
        desc: '活动报名费订单实收'
      },
      {
        title: '现金赞助收入',
        value: '¥0',
        icon: 'Wallet',
        color: '#409EFF',
        desc: '已批准现金赞助实收'
      },
      {
        title: '组织者收入',
        value: '¥0',
        icon: 'Share2',
        color: '#409EFF',
        desc: '活动结算应得（微信发放）'
      },
      {
        title: '平台分成',
        value: '¥0',
        icon: 'Wallet',
        color: '#409EFF',
        desc: '报名费分账+赞助平台分账'
      },
      {
        title: '会员订单收入',
        value: '¥0',
        icon: 'Money',
        color: '#409EFF',
        desc: '会员套餐实收'
      },
      {
        title: '平台净收入',
        value: '¥0',
        icon: 'TrendCharts',
        color: '#409EFF',
        desc: '会员实收+平台分成'
      }
    ])

    // 退款项数据（退款总额、记录数、最近退款明细）
    const refundStats = ref({
      totalRefund: 0,
      count: 0,
      successCount: 0,
      recent: []
    })
    
    // 加载订单列表
    const loadOrders = async () => {
      loading.value = true
      try {
        // 构建查询参数
        const params = {
          page: pagination.current,
          pageSize: pagination.size,
          status: filterParams.status,
          orderType: filterParams.orderType,
          paymentMethod: filterParams.paymentMethod,
          keyword: filterParams.keyword
        }
        
        // 添加时间范围筛选
        if (filterParams.orderDateRange && filterParams.orderDateRange.length === 2) {
          params.startDate = filterParams.orderDateRange[0]
          params.endDate = filterParams.orderDateRange[1]
        }
        
        // 调用真实API
        const response = await api.getOrders(params)
        
        orderList.value = response.data.orders
        pagination.total = response.data.total
        
        // 更新统计数据
        if (response.data.stats) {
          // 收入口径说明（净收入口径）：
          // 平台净收入(totalRevenue) = 会员订单收入(membershipRevenue) + 平台分成(platformRevenue)
          // 平台分成(platformRevenue) = 活动报名费分账(revenue_sharing) + 赞助平台分账(user_financial_details)
          // 活动订单实收(activityOrderRevenue) = 活动报名费订单实收（order_type='activity'，含组织者分成），不属于平台净收入
          // 现金赞助收入(sponsorshipRevenue) = 已批准现金赞助实收（activity_sponsors 表，不生成 orders 记录），单独展示
          // 组织者收入(organizerRevenue) = 活动结算分账给组织者的金额（微信发放），仅作明细展示
          const platformTotalRevenue = (parseFloat(response.data.stats.totalRevenue) || 0).toFixed(2)
          
          orderStats.value = [
            {
              title: '总订单数',
              value: response.data.stats.totalOrders.toString(),
              icon: 'Document',
              color: '#409EFF'
            },
            {
              title: '今日订单',
              value: response.data.stats.todayOrders.toString(),
              icon: 'Calendar',
              color: '#67C23A'
            },
            {
              title: '待支付',
              value: response.data.stats.pendingOrders.toString(),
              icon: 'Clock',
              color: '#E6A23C'
            },
            {
              title: '活动订单实收',
              value: `¥${response.data.stats.activityOrderRevenue || 0}`,
              icon: 'Coin',
              color: '#409EFF',
              desc: '活动报名费订单实收'
            },
            {
              title: '现金赞助收入',
              value: `¥${response.data.stats.sponsorshipRevenue || 0}`,
              icon: 'Wallet',
              color: '#409EFF',
              desc: '已批准现金赞助实收'
            },
            {
              title: '组织者收入',
              value: `¥${response.data.stats.organizerRevenue || 0}`,
              icon: 'Share2',
              color: '#409EFF',
              desc: '活动结算应得（微信发放）'
            },
            {
              title: '平台分成',
              value: `¥${response.data.stats.platformRevenue || 0}`,
              icon: 'Wallet',
              color: '#409EFF',
              desc: '报名费分账+赞助平台分账'
            },
            {
              title: '会员订单收入',
              value: `¥${response.data.stats.membershipRevenue || 0}`,
              icon: 'Money',
              color: '#409EFF',
              desc: '会员套餐实收'
            },
            {
              title: '平台净收入',
              value: `¥${platformTotalRevenue}`,
              icon: 'TrendCharts',
              color: '#409EFF',
              desc: '会员实收+平台分成'
            }
          ]

          // 更新退款项数据
          refundStats.value = response.data.stats.refundStats || {
            totalRefund: 0,
            count: 0,
            successCount: 0,
            recent: []
          }
        }
        
      } catch (error) {
        console.error('加载订单列表失败:', error)
        ElMessage.error('加载订单列表失败')
      } finally {
        loading.value = false
      }
    }
    
    // 处理筛选变化
    const handleFilterChange = () => {
      pagination.current = 1
      loadOrders()
    }
    
    // 处理搜索
    const handleSearch = () => {
      pagination.current = 1
      loadOrders()
    }
    
    // 重置筛选
    const resetFilters = () => {
      Object.assign(filterParams, {
        status: '',
        orderType: '',
        paymentMethod: '',
        orderDateRange: [],
        keyword: ''
      })
      pagination.current = 1
      loadOrders()
    }
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pagination.size = size
      pagination.current = 1
      loadOrders()
    }
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      pagination.current = current
      loadOrders()
    }
    
    // 查看订单详情（跳转至独立订单详情页）
    const handleViewOrder = (order) => {
      router.push({ path: `/orders/${order.id}` })
    }
    
    // 退款订单
    const handleRefundOrder = async (order) => {
      try {
        // 获取用户的默认退款渠道
        let defaultRefundChannel = 'balance';
        try {
          const userResponse = await api.getUserInfo(order.userId);
          if (userResponse.data && userResponse.data.default_refund_channel) {
            defaultRefundChannel = userResponse.data.default_refund_channel;
          }
        } catch (error) {
          console.warn('获取用户默认退款渠道失败:', error);
        }
        
        // 弹出退款确认对话框（使用confirm代替不支持的select类型的prompt）
        const confirmMessage = `
          <div style="text-align: left;">
            <p style="margin-bottom: 16px; font-size: 14px;">订单号：${order.orderNo}</p>
            <p style="margin-bottom: 16px; font-size: 14px;">退款金额：¥${order.totalAmount}</p>
            <p style="margin-bottom: 8px; font-size: 14px; font-weight: 600;">退款渠道：</p>
            <p style="margin-bottom: 4px; padding: 8px; background: #f5f7fa; border-radius: 4px; font-size: 14px;">
              ${defaultRefundChannel === 'balance' ? '✓ 退款到系统账户余额（推荐）' : '○ 退款到系统账户余额（推荐）'}
            </p>
            <p style="color: #909399; font-size: 12px; margin-bottom: 8px;">说明：将直接退款到用户的账户余额，用户可立即使用</p>
            <p style="margin-top: 16px; color: #E6A23C; font-size: 12px;">
              💡 提示：默认退款到系统账户余额，如需原路退回请联系技术支持
            </p>
          </div>
        `;
        
        await ElMessageBox.confirm(
          confirmMessage,
          '退款确认',
          {
            confirmButtonText: '确认退款',
            cancelButtonText: '取消',
            dangerouslyUseHTMLString: true,
            type: 'warning'
          }
        )
        
        // 固定使用余额退款
        const refundChannel = 'balance';
        
        // 获取退款原因
        const { value: reason } = await ElMessageBox.prompt(
          '请输入退款原因：', 
          '退款原因', 
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputPlaceholder: '请输入退款原因',
            inputValue: '管理员操作'
          }
        )
        
        // 处理退款
        await api.processRefund(order.id, { reason, refundChannel })
        ElMessage.success('退款处理成功，已退款到用户账户余额')
        loadOrders() // 重新加载数据
      } catch (error) {
        if (error !== 'cancel') {
          console.error('处理退款失败:', error)
          ElMessage.error('处理退款失败')
        }
      }
    }
    
    // 取消订单
    const handleCancelOrder = async (order) => {
      try {
        await api.updateOrderStatus(order.id, 'cancelled')
        ElMessage.success('订单取消成功')
        loadOrders() // 重新加载数据
      } catch (error) {
        console.error('取消订单失败:', error)
        ElMessage.error('取消订单失败')
      }
    }
    
    // 导出单个订单
    const handleExportOrder = (order) => {
      ElMessage.info(`导出订单: ${order.orderNo}`)
    }
    
    // 导出所有订单
    const handleExportOrders = () => {
      ElMessage.info('导出所有订单数据')
    }
    
    // 工具函数
    const getOrderTypeLabel = (type) => {
      const labels = {
        activity: '活动报名',
        activity_join: '活动报名',
        membership: '会员购买',
        equipment: '装备购买',
        sponsor: '赞助费'
      }
      return labels[type] || type
    }
    
    const getOrderTypeTag = (type) => {
      const tags = {
        activity: 'primary',
        activity_join: 'primary',
        membership: 'success',
        equipment: 'warning',
        registration: 'info',
        sponsor: 'danger',
        recharge: 'success',
        withdrawal: 'warning'
      }
      return tags[type] || 'info'
    }
    
    const getPaymentMethodLabel = (method) => {
      const labels = {
        wechat: '微信支付',
        '微信支付': '微信支付',
        alipay: '支付宝',
        bank: '银行卡',
        balance: '余额支付',
        virtual: '虚拟支付'
      }
      return labels[method] || method
    }
    
    const getPaymentMethodTag = (method) => {
      const tags = {
        wechat: 'success',
        alipay: 'primary',
        bank: 'info'
      }
      return tags[method] || 'info'
    }
    
    const getStatusLabel = (status) => {
      const labels = {
        pending: '待支付',
        paid: '已支付',
        completed: '已完成',
        cancelled: '已取消',
        refunded: '已退款',
        expired: '已过期'
      }
      return labels[status] || status
    }
    
    const getStatusTagType = (status) => {
      const types = {
        pending: 'warning',
        paid: 'success',
        completed: 'info',
        cancelled: 'danger',
        refunded: 'danger',
        expired: 'info'
      }
      return types[status] || 'info'
    }
    
    const formatDateTime = (datetime) => {
      if (!datetime) return ''
      return datetime.replace(' ', ' ')
    }
    
    onMounted(() => {
      loadOrders()
    })
    
    return {
      loading,
      orderList,
      filterParams,
      pagination,
      orderStats,
      refundStats,
      
      handleFilterChange,
      handleSearch,
      resetFilters,
      handleSizeChange,
      handleCurrentChange,
      handleViewOrder,
      handleRefundOrder,
      handleCancelOrder,
      handleExportOrder,
      handleExportOrders,
      
      getOrderTypeLabel,
      getOrderTypeTag,
      getPaymentMethodLabel,
      getPaymentMethodTag,
      getStatusLabel,
      getStatusTagType,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.order-management {
  padding: 20px;
}

/* 复用之前的样式 */
.page-header,
.filter-section,
.stats-grid,
.refund-section,
.list-section,
.table-header,
.pagination-section {
  margin-bottom: 24px;
}

/* 统计卡片网格：大屏 3 列，中屏 2 列，小屏单列 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

/* 所有卡片统一视觉样式：边框、圆角、阴影 */
.stat-card,
.filter-card {
  border: 1px solid #e4e7ed;          /* 统一浅灰边框 */
  border-radius: 10px;                 /* 统一圆角 */
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04); /* 统一阴影 */
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

/* 去除 el-card 默认 body 内边距，由 .stat-content 统一控制 */
.stat-card :deep(.el-card__body) {
  padding: 0;
  width: 100%;
}

.stat-card:hover {
  box-shadow: 0 6px 18px rgba(64, 158, 255, 0.12); /* hover 统一高亮 */
  transform: translateY(-2px);
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 18px;
}

.stat-icon {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 10px;
  background: #ecf5ff;                 /* 统一浅蓝图标底 */
  color: #409eff;                      /* 统一图标主色 */
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.stat-title {
  font-size: 13px;
  color: #909399;
  line-height: 1.3;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-desc {
  font-size: 12px;
  color: #c0c4cc;
  line-height: 1.4;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-no {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-no .label {
  font-size: 12px;
  color: #909399;
}

.order-no .value {
  font-weight: 500;
  color: #303133;
}

.order-user {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  font-size: 12px;
  color: #303133;
}

.user-phone {
  font-size: 12px;
  color: #909399;
}

.order-amount {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.order-amount .total {
  font-weight: 600;
  color: #303133;
}

.order-amount .discount {
  font-size: 11px;
  color: #67c23a;
}

.expand-content {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.items-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e6e8eb;
}

.items-section h4 {
  margin-bottom: 12px;
  color: #303133;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .order-info {
    gap: 4px;
  }
  
  .order-user {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  /* 小屏幕（<768px）：卡片单列，紧凑间距 */
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .stat-content {
    padding: 14px;
    gap: 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
  }

  .stat-value {
    font-size: 20px;
  }

  .stat-title {
    font-size: 12px;
  }

  .stat-desc {
    font-size: 11px;
  }
}

@media (min-width: 768px) and (max-width: 1199px) {
  /* 中等屏幕（平板/小桌面）：2 列网格，适中字号 */
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat-content {
    padding: 16px;
  }

  .stat-value {
    font-size: 22px;
  }
}

@media (min-width: 1200px) {
  /* 大屏幕：3×3 网格，基准样式保持不变 */
}
</style>